import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnyActivity } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { SelectRelativeResult, useSelectRelative } from '@src/utils/hooks/useSelectRelative';
import { useRequiredContext } from '@src/utils/hooks/useRequiredContext';
import { URL_PARAMS } from './activity-feed-constants';
import { ActivityId } from './activity-feed-data-utils';

/**
 * Shared selection state for the ActivityFeed page. Consolidates URL-backed selection
 * (`?selected=<id>`), the derived `selectedActivity` / `selectedIndex`, and the prev/next
 * navigation helper into a single value that's produced once at the page level and consumed
 * anywhere in the Detail column via {@link useSelection}.
 *
 * Selection is typed as {@link AnyActivity} so consumers that only handle events narrow with
 * `selectedActivity.kind === 'event'`.
 */
export interface SelectionValue {
  /** Raw URL-backed id, or null when nothing is selected. */
  selectedId: ActivityId | null;
  /** Position of the selected id in the visible activityIds order, or -1 if not present. */
  selectedIndex: number;
  /** The resolved activity (any kind), or null when nothing is selected. */
  selectedActivity: AnyActivity | null;
  /** Length of the visible activityIds — the nav "N of M" counter's denominator. */
  totalCount: number;
  /** Write the raw URL id. Callers pass a valid id (or null to deselect). */
  setSelectedId: (id: ActivityId | null) => void;
  /** Convenience: `() => setSelectedId(null)`, ready to attach to a close affordance. */
  deselect: () => void;
  /** Clamped forward/backward navigation. See {@link SelectRelativeResult}. */
  selectRelative: SelectRelativeResult;
}

const SelectionContext = createContext<SelectionValue | null>(null);

/**
 * Read the shared ActivityFeed selection state. Throws if used outside a
 * {@link SelectionProvider}; every Detail column caller sits under one, so the throw is
 * intentionally strict — a null return would just get spread into an "of NaN" bug.
 */
export const useSelection = (): SelectionValue =>
  useRequiredContext(SelectionContext, 'useSelection', 'SelectionProvider');

interface SelectionProviderProps {
  activityIds: ActivityId[];
  activitiesById: Record<ActivityId, AnyActivity>;
  children: ReactNode;
}

/**
 * Wire selection state at the page level. Owns the URL-backed selection, derives
 * `selectedIndex` + `selectedActivity` from the ordered `activityIds` + `activitiesById`
 * registry, and constructs `selectRelative` bound to that list. Wrap the ActivityFeed layout
 * in this once and let downstream consumers read {@link useSelection}.
 *
 * `activityIds` should be the full ordered list the user can navigate through (events + other
 * kinds appended), so prev/next steps across every visible card — not just events.
 */
export const SelectionProvider = ({
  activityIds,
  activitiesById,
  children
}: SelectionProviderProps): ReactNode => {
  // Selected activity id lives in the URL search params so refresh / share-link / browser-back
  // preserve the current selection. Uses history replaceState so selecting cards doesn't pile
  // up entries on the back stack.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get(URL_PARAMS.SELECTED);
  const setSelectedId = useCallback(
    (id: ActivityId | null) => {
      setSearchParams(
        params => {
          const next = new URLSearchParams(params);
          if (id !== null) {
            next.set(URL_PARAMS.SELECTED, id);
          } else {
            next.delete(URL_PARAMS.SELECTED);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const selectedIndex = selectedId === null ? -1 : activityIds.indexOf(selectedId);
  // Selection persists across filter/search churn — the Detail column keeps showing the
  // selected activity even when a predicate hides it from the list. The URL id is only
  // cleared by explicit deselect (or when the activity leaves the undismissed universe
  // entirely, at which point activitiesById[selectedId] falls back to null on its own).
  const selectedActivity = selectedId !== null ? (activitiesById[selectedId] ?? null) : null;

  // Route selectByIndex through a ref so the callback identity survives filter/search churn.
  // Rebinding it on every `activityIds` change would rebuild `useSelectRelative`'s callbacks,
  // which would rebuild the SelectionValue memo, forcing every consumer to re-render.
  const activityIdsRef = useRef(activityIds);
  useEffect(() => {
    activityIdsRef.current = activityIds;
  }, [activityIds]);
  const selectByIndex = useCallback(
    (i: number) => setSelectedId(activityIdsRef.current[i]),
    [setSelectedId]
  );
  const selectRelative = useSelectRelative({
    totalCount: activityIds.length,
    onSelect: selectByIndex
  });
  const deselect = useCallback(() => setSelectedId(null), [setSelectedId]);

  const value = useMemo<SelectionValue>(
    () => ({
      selectedId,
      selectedIndex,
      selectedActivity,
      totalCount: activityIds.length,
      setSelectedId,
      deselect,
      selectRelative
    }),
    [
      selectedId,
      selectedIndex,
      selectedActivity,
      activityIds.length,
      setSelectedId,
      deselect,
      selectRelative
    ]
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
};
